import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";
import Accordion, { AccordionPresentationProps, AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import ADIColors from "@NA/Utils/ADIColors";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import cloneDeep from "lodash/cloneDeep";

type Props = WidgetProps & HasProduct;

export interface ADIProductDetailsSpecificationsStyles {
    wrapper?: InjectableCss;
    specificationAccordion?: AccordionPresentationProps;
    specificationAccordionSection?: AccordionSectionPresentationProps;
}

export const adiProductDetailsSpecificationsStyles: ADIProductDetailsSpecificationsStyles = {
    wrapper: {
        css: css`
            margin-bottom: 15px;
            padding-top: 25px;
        `,
    },
    specificationAccordion: {
        css: css`
            // border-bottom: 1px solid #ddd;
            transition: height ease 1s;
            dt[data-test-selector="sectionHeader"] {
                >button[aria-expanded="false"] {
                    border-bottom:1px solid #ddd;
                }
            }
            dt[data-test-selector="sectionHeader"] {
                >button[aria-expanded="true"] {
                    border-bottom:none;
                }
            }
            .spec-table {
                border-collapse: separate;
                border-spacing: 0;
                border-radius: 4px;
                overflow: hidden;
                border-top: 1px solid ${ADIColors.gray2};
            }

            .spec-table tr:first-child th:first-child {
                border-top-left-radius: 4px;
            }

            .spec-table tr:first-child th:last-child {
                border-top-right-radius: 4px;
            }

            .spec-table tr:last-child td:first-child {
                border-bottom-left-radius: 4px;
            }

            .spec-table tr:last-child td:last-child {
                border-bottom-right-radius: 4px;
            }
        `,
    },
    specificationAccordionSection: {
        headerProps: {
            css: css`
                button {
                    background: ${ADIColors.white};
                    border-top: none;
                    border-left: none;
                    border-right: none;
                    flex-direction: row-reverse;
                    justify-content: flex-end;
                    padding: 16px 10px 16px 0px;
                    pointer-events:none;
                    &:focus{
                        border:none;
                        border-bottom:1px solid #DDDDDD;
                    }
                    span {
                        font-family: "Nunito Sans",sans-serif;
                        font-size: 18px!important;
                        font-weight: 600;
                        font-stretch: normal;
                        font-style: normal;
                        line-height: normal;
                        letter-spacing: normal;
                        color: ${ADIColors.darkBlue1} !important;
                        text-transform: capitalize;
                        cursor: pointer;
                        pointer-events:auto;
                    }
                    span[role="presentation"] {
                        margin-right: 0px !important;
                        margin-left: -5px !important;
                        svg {
                            width: 25px;
                            height: 19px;
                        }
                    }
                    &:focus {
                        padding 16px 10px 16px 0px;
                    }

                }
                button[aria-expanded="false"] {
                    span[role="presentation"] {
                        svg {
                            transform: rotate(-90deg);
                        }
                    }
                }
                button[aria-expanded="true"] {
                    span[role="presentation"] {
                        svg {
                            transform: rotate(180deg);
                        }
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 0;
                border: none;
                table {
                    border-collapse: collapse;
                    border: none;
                    border-left: ${ADIColors.gray2} 1px solid;
                    width: 100%;
                    float: none;
                    tr {
                        display: flex;
                        flex-wrap: wrap;
                        border: none;
                        td {
                            width: 25%;
                            min-height: 40px;
                            font-style: normal;
                            font-size: 14px;
                            font-weight: 600;
                            line-height: normal;
                            letter-spacing: normal;
                            padding: 10px 12px 10px 20px;
                            border: none;
                            border-right: ${ADIColors.gray2} 1px solid;
                            border-bottom: ${ADIColors.gray2} 1px solid;
                            background: ${ADIColors.white};
                            &.spec-title {
                                background: ${ADIColors.gray6};
                            }
                            &.spec-value {
                                font-weight: normal;
                            }
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        width: 50%;
                                    `,
                                    css`
                                        width: 50%;
                                    `,
                                    css`
                                        width: 50%;
                                    `,
                                    null,
                                    null,
                                ])}
                            ul {
                                list-style: none;
                                padding-left: 0px;
                                margin: 0px;
                            }
                            &.desktopOnly {
                                ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        display: none;
                                    `,
                                    css`
                                        display: none;
                                    `,
                                    css`
                                        display: none;
                                    `,
                                    null,
                                    null,
                                ])}
                            }
                        }
                    }
                    & + p {
                        display: none;
                    }
                }
            `,
        },
    },
};

const styles = adiProductDetailsSpecificationsStyles;

const ADIProductDetailsSpecifications: React.FC<Props> = ({ product }) => {
    if (!product.specifications) {
        return null;
    }

    let clonedSpecificationObj = cloneDeep(product.specifications);


    clonedSpecificationObj.forEach((eachObj) => {
        let newHtmlData = "";
        let a = eachObj?.htmlContent.indexOf("<tr>");
        let b = eachObj?.htmlContent.indexOf("</tr>");

        // regex to accept new line character
        let regex = /<td\b[^>]*>([\s\S]*?)<\/td>/g;

        let tdMatches: any[] = [];
        let tdObjArr: any[] = [];
        let match;

        while ((match = regex.exec(eachObj.htmlContent)) !== null) {
            tdMatches.push(match[0]);
        }
        for (let i = 0; i < tdMatches.length; i = i + 2) {
            if (i === 0) {
                tdObjArr.push(tdMatches[i])
                tdObjArr.push(tdMatches[i + 1])
            }
            else {
                if (tdMatches[i - 2] !== tdMatches[i]) {
                    tdObjArr.push(tdMatches[i]);
                    tdObjArr.push(tdMatches[i + 1]);
                }
                else {
                    let oldStr = tdMatches[i - 1].match(/<td class="spec-value">(.*?)<\/td>/);
                    let newStr = tdMatches[i + 1].match(/<td class="spec-value">(.*?)<\/td>/);
                    let finalStr: any = `<td class="spec-value">${oldStr && oldStr[1]}, ${newStr && newStr[1]}</td>`
                    tdMatches[i + 1] = finalStr;
                    if (tdMatches[i + 2] !== tdMatches[i]) {
                        tdObjArr.push(tdMatches[i]);
                        tdObjArr.push(finalStr);
                    }
                }
            }
        }
        if (tdObjArr.length % 4 !== 0) {
            tdObjArr.push('<td class="spec-title desktopOnly"></td>');
            tdObjArr.push('<td class="spec-value desktopOnly"></td>');
        }
        tdObjArr.forEach(tdObj => {
            newHtmlData += tdObj;
        });
        eachObj.htmlContent = eachObj?.htmlContent.slice(0, a + 4) + newHtmlData + eachObj?.htmlContent.slice(b);
    })

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography style={{marginBottom: "auto"}} variant="h3" as="h3" data-test-selector="SpecificationsTitle">
                {translate("Specifications")}
            </Typography>
            {clonedSpecificationObj.slice().sort((spec1: any, spec2: any) => spec1?.sortOrder - spec2?.sortOrder).map((spec: any, index: number) => (
                <Accordion key={spec.id} headingLevel={3} {...styles.specificationAccordion}>
                    <ManagedAccordionSection
                        initialExpanded={index < 4 ? true : false}
                        title={spec.name}
                        {...styles.specificationAccordionSection}
                    >{parse(spec.htmlContent, parserOptions)}
                    </ManagedAccordionSection>
                </Accordion>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ADIProductDetailsSpecifications),
    definition: {
        displayName: "ADI Specifications",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
